// src/task.ts
function deriveTaskStateMode(profile) {
  if (!profile) return "neutral";
  const pRequired = optionalScore(profile.required_productivity_energy);
  const rjRequired = optionalScore(profile.required_rejuvenation_energy);
  const pReturn = optionalScore(profile.productivity_return);
  const rjReturn = optionalScore(profile.rejuvenation_return);
  const pNet = (pReturn ?? 0) - (pRequired ?? 0);
  const rjNet = (rjReturn ?? 0) - (rjRequired ?? 0);
  const pActive = pNet > 0;
  const rjActive = rjNet > 0;
  if (pActive && rjActive) return "both";
  if (pActive) return "productive";
  if (rjActive) return "rejuvenating";
  return "neutral";
}
function optionalScore(value) {
  if (value === null || value === void 0 || value === "") return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.min(10, numeric));
}
function taskToUI(task) {
  if (task?.task) return taskToUI(task.task);
  if (task.core_fields) {
    return {
      id: task.id,
      userId: task.user_id,
      parentId: task.parent_id,
      bundleId: task.bundle_id ?? null,
      projectId: task.project_id ?? null,
      teamId: task.team_id ?? null,
      assignedToUserId: task.assigned_to_user_id ?? null,
      createdByUserId: task.created_by_user_id ?? null,
      title: task.core_fields.title,
      description: task.core_fields.description,
      status: task.core_fields.status,
      priority: priorityToNumber(task.core_fields.priority),
      depth: 0,
      // Not explicitly stored in KV document top level
      position: task.position,
      estimatedDuration: task.core_fields.duration_minutes,
      remainingDuration: task.core_fields.remaining_duration_minutes ?? task.core_fields.duration_minutes,
      completionPercent: task.core_fields.completion_percent ?? 0,
      deadline: task.core_fields.due_date,
      scheduledStart: task.core_fields.do_date,
      scheduledEnd: task.core_fields.do_date_end,
      completedAt: task.core_fields.status === "completed" ? task.updated_at : null,
      energyLevel: task.ai_metadata?.predicted_energy || void 0,
      stateProfile: task.state_profile || void 0,
      customAttributes: task.custom_attributes || {},
      isRecurring: false,
      recurrenceRule: null,
      createdAt: task.created_at,
      updatedAt: task.updated_at
    };
  }
  return {
    id: task.id,
    userId: task.user_id,
    parentId: task.parent_id,
    bundleId: task.bundle_id ?? null,
    projectId: task.project_id ?? null,
    teamId: task.team_id ?? null,
    assignedToUserId: task.assigned_to_user_id ?? null,
    createdByUserId: task.created_by_user_id ?? null,
    title: task.title,
    status: task.status,
    priority: priorityToNumber(task.priority),
    depth: task.depth,
    position: task.position,
    estimatedDuration: task.estimated_duration,
    remainingDuration: task.remaining_duration ?? task.estimated_duration,
    completionPercent: task.completion_percent ?? 0,
    deadline: task.due_date,
    scheduledStart: task.scheduled_start,
    scheduledEnd: task.scheduled_end,
    completedAt: task.completed_at,
    isRecurring: task.is_recurring,
    recurrenceRule: task.recurrence_rule,
    createdAt: task.created_at,
    updatedAt: task.updated_at,
    customAttributes: task.custom_attributes || {}
  };
}
function priorityToNumber(priority) {
  const map = {
    urgent: 1,
    high: 2,
    medium: 3,
    low: 4
  };
  return map[priority] || 3;
}
function numberToPriority(num) {
  const map = {
    1: "urgent",
    2: "high",
    3: "medium",
    4: "low",
    5: "low"
  };
  return map[num] || "medium";
}
function createDefaultCoreFields(title) {
  return {
    title,
    description: void 0,
    status: "todo",
    due_date: null,
    do_date: null,
    do_date_end: null,
    duration_minutes: 30,
    remaining_duration_minutes: 30,
    completion_percent: 0,
    priority: "medium",
    is_flexible: true,
    is_immovable: false
  };
}
function createDefaultAIMetadata() {
  return {
    predicted_energy: 5,
    predicted_emotion: "neutral",
    confidence_score: 0,
    is_ghost_block: false,
    last_scheduled_at: null,
    scheduling_reason: null
  };
}

// src/schedule.ts
var DEFAULT_USER_WEIGHTS = {
  w1_front_loading: 0.1,
  w2_energy: 2,
  w3_deadline: 1.5,
  deadline_spike: 10,
  deadline_pull: 0.05,
  energy_threshold: 5,
  break_penalty: 0.5
};

// src/user.ts
var DEFAULT_USER_SETTINGS = {
  theme: "system",
  default_view: "week",
  work_hours: {
    start: "09:00",
    end: "17:00"
  },
  work_days: [1, 2, 3, 4, 5],
  // Monday-Friday
  notification_preferences: {
    email: true,
    push: true,
    infeasibility_alerts: true,
    scheduling_suggestions: true
  },
  ai_preferences: {
    auto_schedule: true,
    suggest_ghost_blocks: true,
    energy_tracking: true
  }
};

// src/attribute.ts
var SYSTEM_ATTRIBUTES = [
  {
    name: "Energy Required",
    data_type: "integer",
    constraints: { min: 1, max: 10, default_value: 5 },
    math_map: null,
    weight: 2
  },
  {
    name: "Focus Level",
    data_type: "integer",
    constraints: { min: 1, max: 10, default_value: 5 },
    math_map: null,
    weight: 1.5
  },
  {
    name: "Emotion Word",
    data_type: "string",
    constraints: { default_value: "neutral" },
    math_map: null,
    weight: 1
  },
  {
    name: "Net Rejuvenation",
    data_type: "integer",
    constraints: { min: -10, max: 10, default_value: 0 },
    math_map: null,
    weight: 1
  },
  {
    name: "Net Productivity",
    data_type: "integer",
    constraints: { min: -10, max: 10, default_value: 0 },
    math_map: null,
    weight: 1
  }
];

// src/workspace.ts
function projectToUI(project) {
  return {
    id: project.id,
    userId: project.user_id,
    teamId: project.team_id ?? null,
    createdByUserId: project.created_by_user_id ?? null,
    name: project.name,
    purpose: project.purpose,
    color: project.color,
    priorityScore: project.priority_score ?? null,
    status: project.status,
    createdAt: project.created_at,
    updatedAt: project.updated_at
  };
}
function bundleToUI(bundle) {
  return {
    id: bundle.id,
    userId: bundle.user_id,
    teamId: bundle.team_id ?? null,
    createdByUserId: bundle.created_by_user_id ?? null,
    projectId: bundle.project_id,
    name: bundle.name,
    purpose: bundle.purpose,
    color: bundle.color,
    priorityScore: bundle.priority_score ?? null,
    status: bundle.status,
    isDefault: Boolean(bundle.is_default),
    createdAt: bundle.created_at,
    updatedAt: bundle.updated_at
  };
}
export {
  DEFAULT_USER_SETTINGS,
  DEFAULT_USER_WEIGHTS,
  SYSTEM_ATTRIBUTES,
  bundleToUI,
  createDefaultAIMetadata,
  createDefaultCoreFields,
  deriveTaskStateMode,
  numberToPriority,
  priorityToNumber,
  projectToUI,
  taskToUI
};
